import { PaymentType } from '@afterpay/types'
import { isAfterpayInAppBrowser } from '../../utils/device'
import { Asset, AssetId, Features, PostCheckoutFlow } from '../../model'
import { useFlag } from '@afterpaytouch/feature'
import { getAssetImpressions, getCtaTagName } from '../../utils/post-checkout'
import { useConsumerTag, useIsEligibleForGiftCards, useMarketingOptIn, useSelectedPaymentType, useCohort } from '..'
import { Cohort } from '@afterpaytouch/portal-api'

export const usePersonalizationEnabled = (): boolean => useFlag(Features.ENABLED_PERSONALIZATION, false)

export const usePostCheckoutFlow = (): PostCheckoutFlow => useFlag<PostCheckoutFlow>(Features.POST_CHECKOUT_CAPTURE_TYPE, PostCheckoutFlow.PreCapture)

export const useBrowserEligible = (): boolean => !isAfterpayInAppBrowser()

export const useOrderEligible = (): boolean => {
  const paymentType = useSelectedPaymentType()
  const isEligible = paymentType !== PaymentType.PCL
  return isEligible
}

export const useConsumerEligible = (id: AssetId): boolean => {
  const isOptedIntoMarketing = useMarketingOptIn() === 'SUBSCRIBED'
  const isEligibleForGiftCards = useIsEligibleForGiftCards()
  const isCashCohort = useCohort() === Cohort.CashOnly

  // Add your conditions here
  const MAP_OF_CONSUMER_ELIGIBILITY_CONDITIONS: Record<AssetId, boolean[]> = {
    [AssetId.Dummy]: [],
    [AssetId.Survey202303]: [isOptedIntoMarketing],
    [AssetId.GiftCard202306]: [isOptedIntoMarketing, isEligibleForGiftCards],
    [AssetId.GoogleSSO]: [],
    [AssetId.MarketingOptIn202308]: [!isOptedIntoMarketing],
    [AssetId.CashAppOrderServicingMessage202503]: [isCashCohort],
  }

  const predicate = (prevValues: boolean[], currValue: boolean): boolean[] => [...prevValues, currValue]

  const arrayOfConditions = MAP_OF_CONSUMER_ELIGIBILITY_CONDITIONS[id].reduce(predicate, [])

  return arrayOfConditions.every(Boolean)
}

export const useAssetEligible = (asset: Asset): boolean => {
  const tags = useConsumerTag()

  if (useFlag(Features.IGNORE_ASSET_IMPRESSIONS)) {
    return true
  }

  const { id, maxImpressions } = asset
  const hasCta = tags[getCtaTagName(id)] !== undefined

  if (hasCta) {
    return false
  }

  const impressions = getAssetImpressions(id, tags)
  const hasImpressionsLeft = impressions < maxImpressions

  return hasImpressionsLeft
}
